import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

const SEOHead = ({ 
  title = "MahStry - Best School ERP & College Management Software | #1 Education Platform India",
  description = "MahStry is India's #1 School ERP, College Management System & Coaching Software. Complete institute management with admissions, fee management, attendance, timetable, student management. Also practice mock tests for CAT, JEE, NEET, UPSC, SSC & more.",
  keywords = "school ERP, best school ERP, school ERP software, school management software, college ERP, college management system, coaching software, coaching institute software, institute management system, education ERP, student management software, fee management system, attendance management, online admission software, timetable management, school software, coaching management, online study, mock tests, competitive exams, study platform, exam preparation, CAT preparation, JEE preparation, NEET preparation, UPSC preparation, SSC preparation, banking exams, admit card, result, study rooms, online courses, test series, practice tests, exam coaching, study materials, previous year papers, sample papers, study groups, collaborative learning, exam tips, study strategies, performance analytics, exam calendar, notification, syllabus, study plan, revision notes, flashcards, doubt resolution, expert guidance, exam coaching online, best study app, top education platform, India's leading study platform, school fees collection, student attendance app, online classes software, live class platform, education technology, edtech India",
  canonical,
  ogImage = "https://mahstry.com/icon-512x512.png",
  twitterImage,
  structuredData = null,
  noIndex = false,
  type = "website",
  publishedTime,
  modifiedTime,
  section,
  tags = [],
  author = "MahStry"
}) => {
  const location = useLocation();
  const baseUrl = 'https://mahstry.com';
  const currentUrl = canonical || `${baseUrl}${location.pathname}`;
  const fullTitle = title.includes('MahStry') ? title : `${title} | MahStry`;
  const finalOgImage = ogImage.startsWith('http') ? ogImage : `${baseUrl}${ogImage}`;
  const finalTwitterImage = twitterImage || finalOgImage;

  // Default structured data for organization
  const defaultStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'MahStry',
    url: baseUrl,
    logo: `${baseUrl}/icon-512x512.png`,
    description: 'India\'s #1 School ERP, College Management System & Coaching Software. Complete institute management with admissions, fee management, attendance, timetable, student management. Also practice mock tests for competitive exams.',
    sameAs: [
      'https://twitter.com/mahstry',
      'https://facebook.com/mahstry',
      'https://linkedin.com/company/mahstry',
      'https://instagram.com/mahstry',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'Customer Service',
      email: 'support@mahstry.com',
      availableLanguage: ['English', 'Hindi'],
    },
    areaServed: {
      '@type': 'Country',
      name: 'India',
    },
  };

  // Website structured data
  const websiteStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'MahStry',
    alternateName: ['MahStry School ERP', 'MahStry College Management', 'MahStry Coaching Software'],
    url: baseUrl,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${baseUrl}/search?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };

  // Educational service structured data
  const educationalServiceData = {
    '@context': 'https://schema.org',
    '@type': 'EducationalOrganization',
    name: 'MahStry',
    url: baseUrl,
    description: 'School ERP, College Management System, Coaching Software, and Online education platform for competitive exam preparation',
    educationalCredentialAwarded: 'Certificate of Completion',
    offers: {
      '@type': 'Offer',
      category: 'Education Software',
      availability: 'https://schema.org/InStock',
    },
  };

  // Breadcrumb structured data
  const breadcrumbData = location.pathname !== '/' ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: baseUrl,
      },
      ...location.pathname.split('/').filter(Boolean).map((segment, index) => ({
        '@type': 'ListItem',
        position: index + 2,
        name: segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' '),
        item: `${baseUrl}/${location.pathname.split('/').slice(0, index + 2).join('/')}`,
      })),
    ],
  } : null;

  // Article structured data (if publishedTime is provided)
  const articleData = publishedTime ? {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: fullTitle,
    description: description,
    image: finalOgImage,
    datePublished: publishedTime,
    dateModified: modifiedTime || publishedTime,
    author: {
      '@type': 'Organization',
      name: author,
    },
    publisher: {
      '@type': 'Organization',
      name: 'MahStry',
      logo: {
        '@type': 'ImageObject',
        url: `${baseUrl}/icon-512x512.png`,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': currentUrl,
    },
    ...(section && { articleSection: section }),
    ...(tags.length > 0 && { keywords: tags.join(', ') }),
  } : null;

  // Handle structuredData as either single object or array
  const structuredDataItems = Array.isArray(structuredData) ? structuredData : (structuredData ? [structuredData] : []);
  
  const structuredDataArray = [
    defaultStructuredData,
    websiteStructuredData,
    educationalServiceData,
    breadcrumbData,
    articleData,
    ...structuredDataItems,
  ].filter(Boolean);
  
  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <meta name="publisher" content="MahStry" />
      <meta name="robots" content={noIndex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"} />
      <meta name="googlebot" content={noIndex ? "noindex, nofollow" : "index, follow"} />
      <meta name="bingbot" content={noIndex ? "noindex, nofollow" : "index, follow"} />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="1 days" />
      <meta name="distribution" content="global" />
      <meta name="rating" content="general" />
      <meta name="geo.region" content="IN" />
      <meta name="geo.placename" content="India" />
      <meta name="theme-color" content="#2563eb" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={finalOgImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={fullTitle} />
      <meta property="og:site_name" content="MahStry" />
      <meta property="og:locale" content="en_IN" />
      <meta property="og:locale:alternate" content="hi_IN" />
      {publishedTime && <meta property="og:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="og:modified_time" content={modifiedTime} />}
      {section && <meta property="article:section" content={section} />}
      {tags.length > 0 && tags.map((tag, index) => (
        <meta key={index} property="article:tag" content={tag} />
      ))}
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={finalTwitterImage} />
      <meta name="twitter:image:alt" content={fullTitle} />
      <meta name="twitter:creator" content="@mahstry" />
      <meta name="twitter:site" content="@mahstry" />
      
      {/* Additional SEO Meta Tags */}
      <meta name="application-name" content="MahStry" />
      <meta name="apple-mobile-web-app-title" content="MahStry" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="msapplication-TileColor" content="#2563eb" />
      <meta name="msapplication-TileImage" content={`${baseUrl}/icon-192x192.png`} />
      <meta name="msapplication-config" content={`${baseUrl}/browserconfig.xml`} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={currentUrl} />
      
      {/* Preconnect for Performance */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="dns-prefetch" href="https://www.google-analytics.com" />
      <link rel="dns-prefetch" href="https://www.googletagmanager.com" />
      
      {/* Structured Data (JSON-LD) */}
      {structuredDataArray.map((data, index) => (
        <script
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
        />
      ))}
    </Helmet>
  );
};

export default SEOHead;
